import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { AdminLayout } from "./AdminDashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { MediaUploader, MediaDisplay } from "@/components/MediaComponents";
import { Plus, Edit, Trash2, FileText, Eye, Clock, Share2, Facebook, Twitter, Linkedin } from "lucide-react";
import { format } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const categories = ["news", "events", "awareness", "community", "health"];

export default function AdminBlog() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [scheduledAt, setScheduledAt] = useState("");
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    excerpt: "",
    image_url: "",
    media: [],
    category: "news",
    tags: "",
    featured: false,
    published: false
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      const response = await axios.get(`${API}/blog?published=false`, { withCredentials: true });
      setBlogs(response.data);
    } catch (error) {
      console.error("Error fetching blogs:", error);
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setSelectedBlog(null);
    setFormData({
      title: "",
      content: "",
      excerpt: "",
      image_url: "",
      category: "news",
      tags: "",
      featured: false,
      published: false
    });
    setDialogOpen(true);
  };

  const openEditDialog = (blog) => {
    setSelectedBlog(blog);
    setFormData({
      title: blog.title,
      content: blog.content,
      excerpt: blog.excerpt || "",
      image_url: blog.image_url || "",
      media: blog.media || [],
      category: blog.category,
      tags: blog.tags?.join(", ") || "",
      featured: blog.featured,
      published: blog.published
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const payload = {
        ...formData,
        tags: formData.tags.split(",").map(t => t.trim()).filter(Boolean)
      };

      if (selectedBlog) {
        await axios.put(`${API}/blog/${selectedBlog.blog_id}`, payload, { withCredentials: true });
        toast.success("Blog post updated successfully");
      } else {
        await axios.post(`${API}/blog`, payload, { withCredentials: true });
        toast.success("Blog post created successfully");
      }

      setDialogOpen(false);
      fetchBlogs();
    } catch (error) {
      console.error("Error saving blog:", error);
      toast.error(error.response?.data?.detail || "Failed to save blog post");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedBlog) return;

    try {
      await axios.delete(`${API}/blog/${selectedBlog.blog_id}`, { withCredentials: true });
      toast.success("Blog post deleted successfully");
      setDeleteDialogOpen(false);
      setSelectedBlog(null);
      fetchBlogs();
    } catch (error) {
      console.error("Error deleting blog:", error);
      toast.error("Failed to delete blog post");
    }
  };

  const handleSchedule = async () => {
    if (!selectedBlog || !scheduledAt) return;
    
    try {
      await axios.put(`${API}/blogs/${selectedBlog.blog_id}/schedule`, {
        scheduled_at: new Date(scheduledAt).toISOString()
      }, { withCredentials: true });
      toast.success("Blog post scheduled successfully");
      setScheduleDialogOpen(false);
      fetchBlogs();
    } catch (error) {
      console.error("Error scheduling blog:", error);
      toast.error("Failed to schedule blog post");
    }
  };

  const handleShareToSocial = async () => {
    if (!selectedBlog || selectedPlatforms.length === 0) return;
    
    try {
      const response = await axios.post(`${API}/blogs/${selectedBlog.blog_id}/share-social`, {
        platforms: selectedPlatforms
      }, { withCredentials: true });
      toast.success("Share request sent!");
      setShareDialogOpen(false);
      setSelectedPlatforms([]);
    } catch (error) {
      console.error("Error sharing to social:", error);
      toast.error("Failed to share to social media");
    }
  };

  const togglePlatform = (platform) => {
    if (selectedPlatforms.includes(platform)) {
      setSelectedPlatforms(prev => prev.filter(p => p !== platform));
    } else {
      setSelectedPlatforms(prev => [...prev, platform]);
    }
  };

  return (
    <AdminLayout currentPage="Blog">
      <div className="p-8" data-testid="admin-blog">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-heading-2 text-deep-blue">Blog Posts</h1>
            <p className="text-slate-600">Manage your news and blog content</p>
          </div>
          <Button className="btn-primary" onClick={openCreateDialog} data-testid="create-blog-btn">
            <Plus className="w-4 h-4 mr-2" />
            Create Post
          </Button>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white rounded-lg p-6 h-24 animate-pulse"></div>
            ))}
          </div>
        ) : blogs.length > 0 ? (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left p-4 font-semibold text-slate-700">Title</th>
                  <th className="text-left p-4 font-semibold text-slate-700">Category</th>
                  <th className="text-left p-4 font-semibold text-slate-700">Status</th>
                  <th className="text-left p-4 font-semibold text-slate-700">Views</th>
                  <th className="text-left p-4 font-semibold text-slate-700">Date</th>
                  <th className="text-left p-4 font-semibold text-slate-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {blogs.map(blog => (
                  <tr key={blog.blog_id} data-testid={`blog-row-${blog.blog_id}`}>
                    <td className="p-4">
                      <div>
                        <p className="font-medium text-slate-800">{blog.title}</p>
                        {blog.featured && (
                          <span className="text-xs bg-awareness-blue/10 text-awareness-blue px-2 py-0.5 rounded">
                            Featured
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-4">
                      <span className="capitalize bg-slate-100 px-2 py-1 rounded text-sm">
                        {blog.category}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded text-sm ${
                        blog.published 
                          ? 'bg-green-100 text-green-700' 
                          : blog.status === 'scheduled'
                          ? 'bg-purple-100 text-purple-700'
                          : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {blog.published ? 'Published' : blog.status === 'scheduled' ? 'Scheduled' : 'Draft'}
                      </span>
                    </td>
                    <td className="p-4 text-slate-600">{blog.views}</td>
                    <td className="p-4 text-slate-600">
                      {format(new Date(blog.created_at), "MMM d, yyyy")}
                    </td>
                    <td className="p-4">
                      <div className="flex gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => openEditDialog(blog)}
                          data-testid={`edit-blog-${blog.blog_id}`}
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        {!blog.published && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => { setSelectedBlog(blog); setScheduledAt(""); setScheduleDialogOpen(true); }}
                            title="Schedule"
                          >
                            <Clock className="w-4 h-4 text-purple-600" />
                          </Button>
                        )}
                        {blog.published && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => { setSelectedBlog(blog); setSelectedPlatforms([]); setShareDialogOpen(true); }}
                            title="Share to Social"
                          >
                            <Share2 className="w-4 h-4 text-awareness-blue" />
                          </Button>
                        )}
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => { setSelectedBlog(blog); setDeleteDialogOpen(true); }}
                          data-testid={`delete-blog-${blog.blog_id}`}
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-white rounded-lg p-12 text-center">
            <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">No blog posts yet</h3>
            <p className="text-slate-500 mb-4">Create your first blog post to get started</p>
            <Button className="btn-primary" onClick={openCreateDialog}>
              <Plus className="w-4 h-4 mr-2" />
              Create Post
            </Button>
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedBlog ? "Edit Blog Post" : "Create Blog Post"}</DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="title">Title *</Label>
                <Input 
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  required
                  data-testid="blog-title"
                />
              </div>

              <div>
                <Label htmlFor="excerpt">Excerpt</Label>
                <Textarea 
                  id="excerpt"
                  value={formData.excerpt}
                  onChange={(e) => setFormData({...formData, excerpt: e.target.value})}
                  rows={2}
                  placeholder="A brief summary of the post"
                />
              </div>

              <div>
                <Label htmlFor="content">Content *</Label>
                <Textarea 
                  id="content"
                  value={formData.content}
                  onChange={(e) => setFormData({...formData, content: e.target.value})}
                  rows={10}
                  required
                  data-testid="blog-content"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="category">Category</Label>
                  <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <MediaUploader value={formData.media || []} onChange={(m) => setFormData(prev => ({...prev, media: m}))} label="Blog Media (photos/video slideshow)" />
              </div>

              <div>
                <Label htmlFor="tags">Tags (comma-separated)</Label>
                <Input 
                  id="tags"
                  value={formData.tags}
                  onChange={(e) => setFormData({...formData, tags: e.target.value})}
                  placeholder="awareness, health, community"
                />
              </div>

              <div className="flex gap-6">
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="published"
                    checked={formData.published}
                    onCheckedChange={(checked) => setFormData({...formData, published: checked})}
                  />
                  <Label htmlFor="published" className="cursor-pointer">Published</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="featured"
                    checked={formData.featured}
                    onCheckedChange={(checked) => setFormData({...formData, featured: checked})}
                  />
                  <Label htmlFor="featured" className="cursor-pointer">Featured</Label>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="btn-primary" disabled={saving} data-testid="save-blog-btn">
                  {saving ? "Saving..." : selectedBlog ? "Update Post" : "Create Post"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Blog Post</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete "{selectedBlog?.title}"? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Schedule Dialog */}
        <Dialog open={scheduleDialogOpen} onOpenChange={setScheduleDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-purple-600" />
                Schedule Blog Post
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-slate-600">
                Schedule "{selectedBlog?.title}" to be published automatically.
              </p>
              <div>
                <Label htmlFor="scheduledAt">Publish Date & Time</Label>
                <Input
                  id="scheduledAt"
                  type="datetime-local"
                  value={scheduledAt}
                  onChange={(e) => setScheduledAt(e.target.value)}
                  min={new Date().toISOString().slice(0, 16)}
                  className="mt-1"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => setScheduleDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSchedule} disabled={!scheduledAt} className="bg-purple-600 hover:bg-purple-700 text-white">
                  <Clock className="w-4 h-4 mr-2" />
                  Schedule Post
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Share to Social Dialog */}
        <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Share2 className="w-5 h-5 text-awareness-blue" />
                Share to Social Media
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-slate-600">
                Share "{selectedBlog?.title}" to your connected social media accounts.
              </p>
              <div className="space-y-3">
                <button
                  onClick={() => togglePlatform("facebook")}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all ${
                    selectedPlatforms.includes("facebook") 
                      ? "border-blue-500 bg-blue-50" 
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                    <Facebook className="w-5 h-5 text-white" />
                  </div>
                  <span className="font-medium">Facebook</span>
                  {selectedPlatforms.includes("facebook") && (
                    <span className="ml-auto text-blue-600">✓</span>
                  )}
                </button>
                <button
                  onClick={() => togglePlatform("twitter")}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all ${
                    selectedPlatforms.includes("twitter") 
                      ? "border-black bg-slate-50" 
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <div className="w-10 h-10 bg-black rounded-lg flex items-center justify-center">
                    <Twitter className="w-5 h-5 text-white" />
                  </div>
                  <span className="font-medium">Twitter/X</span>
                  {selectedPlatforms.includes("twitter") && (
                    <span className="ml-auto">✓</span>
                  )}
                </button>
                <button
                  onClick={() => togglePlatform("linkedin")}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all ${
                    selectedPlatforms.includes("linkedin") 
                      ? "border-blue-700 bg-blue-50" 
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <div className="w-10 h-10 bg-blue-700 rounded-lg flex items-center justify-center">
                    <Linkedin className="w-5 h-5 text-white" />
                  </div>
                  <span className="font-medium">LinkedIn</span>
                  {selectedPlatforms.includes("linkedin") && (
                    <span className="ml-auto text-blue-700">✓</span>
                  )}
                </button>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => setShareDialogOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleShareToSocial} 
                  disabled={selectedPlatforms.length === 0} 
                  className="btn-primary"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Share to {selectedPlatforms.length} Platform{selectedPlatforms.length !== 1 ? 's' : ''}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
